/**
 * Offline DSP. Everything here runs on plain Float32Arrays, never on the AudioContext, so
 * the one-shot bank can be baked identically on any machine at a fixed sample rate and
 * WITHOUT an AudioContext existing (which matters: in the headless harness one never does).
 *
 * DETERMINISM: nothing in this file calls Math.random. Randomness comes from a pre-drawn
 * table (see banks.js `RandTable`) that is filled once in AudioSystem.init() from
 * ctx.rng.fork('audio'). That keeps the RNG draw count identical whether or not audio ever
 * boots, which is what ARCHITECTURE.md §6 actually requires.
 */

const TAU = Math.PI * 2;

/** Deterministic pseudo-random source: a pre-filled table with a wrapping cursor. */
export class RandTable {
  constructor(values) {
    this.v = values;          // Float32Array of uniforms in [0,1)
    this.i = 0;
  }
  /** [0,1) */
  next() { const x = this.v[this.i]; this.i = (this.i + 1) % this.v.length; return x; }
  /** [-1,1) */
  bi() { return this.next() * 2 - 1; }
  range(a, b) { return a + this.next() * (b - a); }
  /** Rewind so a given bake always starts from the same place. */
  seekTo(i) { this.i = i % this.v.length; }
}

// --------------------------------------------------------------------- envelopes

/** Exponential decay with a short linear attack. t in seconds. */
export function ad(t, attack, tau) {
  if (t < 0) return 0;
  const a = attack > 0 ? Math.min(1, t / attack) : 1;
  return a * Math.exp(-Math.max(0, t - attack) / tau);
}

/** Attack / hold / exponential release. */
export function ahr(t, attack, hold, tau) {
  if (t < 0) return 0;
  if (t < attack) return t / attack;
  if (t < attack + hold) return 1;
  return Math.exp(-(t - attack - hold) / tau);
}

/** Exponential glide from a to b with time constant tau. */
export function glide(t, a, b, tau) {
  return b + (a - b) * Math.exp(-Math.max(0, t) / tau);
}

/** Smooth 0→1 ramp. */
export function smoothstep(e0, e1, x) {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

// --------------------------------------------------------------------- filters

/**
 * Direct-form-I biquad, RBJ cookbook coefficients. Coefficients can be recomputed mid-run
 * (see `SweptBiquad`) which is how the swept-resonance impact sounds are made.
 */
export class Biquad {
  constructor(sr) { this.sr = sr; this.reset(); this.b0 = 1; this.b1 = 0; this.b2 = 0; this.a1 = 0; this.a2 = 0; }
  reset() { this.x1 = 0; this.x2 = 0; this.y1 = 0; this.y2 = 0; }

  _set(b0, b1, b2, a0, a1, a2) {
    const ia = 1 / a0;
    this.b0 = b0 * ia; this.b1 = b1 * ia; this.b2 = b2 * ia;
    this.a1 = a1 * ia; this.a2 = a2 * ia;
  }

  lowpass(f, q) {
    const w = TAU * Math.min(f, this.sr * 0.49) / this.sr;
    const c = Math.cos(w), s = Math.sin(w), al = s / (2 * q);
    this._set((1 - c) / 2, 1 - c, (1 - c) / 2, 1 + al, -2 * c, 1 - al);
    return this;
  }
  highpass(f, q) {
    const w = TAU * Math.min(f, this.sr * 0.49) / this.sr;
    const c = Math.cos(w), s = Math.sin(w), al = s / (2 * q);
    this._set((1 + c) / 2, -(1 + c), (1 + c) / 2, 1 + al, -2 * c, 1 - al);
    return this;
  }
  bandpass(f, q) {
    const w = TAU * Math.min(f, this.sr * 0.49) / this.sr;
    const c = Math.cos(w), s = Math.sin(w), al = s / (2 * q);
    this._set(al, 0, -al, 1 + al, -2 * c, 1 - al);
    return this;
  }
  peaking(f, q, dbGain) {
    const A = Math.pow(10, dbGain / 40);
    const w = TAU * Math.min(f, this.sr * 0.49) / this.sr;
    const c = Math.cos(w), s = Math.sin(w), al = s / (2 * q);
    this._set(1 + al * A, -2 * c, 1 - al * A, 1 + al / A, -2 * c, 1 - al / A);
    return this;
  }

  step(x) {
    const y = this.b0 * x + this.b1 * this.x1 + this.b2 * this.x2 - this.a1 * this.y1 - this.a2 * this.y2;
    this.x2 = this.x1; this.x1 = x;
    this.y2 = this.y1; this.y1 = y;
    return y;
  }

  /** In-place filter of a whole buffer. */
  run(buf, from = 0, to = buf.length) {
    for (let i = from; i < to; i++) buf[i] = this.step(buf[i]);
    return buf;
  }
}

/**
 * A biquad whose cutoff is recomputed every `block` samples from a callback. This is how a
 * collision crunch gets its downward resonance sweep without needing an OfflineAudioContext.
 */
export function sweptFilter(buf, sr, type, freqAt, q, block = 32) {
  const bq = new Biquad(sr);
  for (let i = 0; i < buf.length; i += block) {
    const t = i / sr;
    const f = freqAt(t);
    if (type === 'lowpass') bq.lowpass(f, q);
    else if (type === 'highpass') bq.highpass(f, q);
    else bq.bandpass(f, q);
    const end = Math.min(buf.length, i + block);
    for (let j = i; j < end; j++) buf[j] = bq.step(buf[j]);
  }
  return buf;
}

// --------------------------------------------------------------------- sources

/**
 * Add a partial with an amplitude envelope.
 *
 *   { freq: 440 }            constant frequency  — fast path
 *   { freqAt: (t) => ... }   swept frequency     — general path
 *
 * The constant-frequency sine case uses a phasor ROTATION rather than a Math.sin per
 * sample. The bells are the expensive part of the bank (chimePerfect alone is 30 partials
 * across 137 k samples) and this took the whole bake from 728 ms to well under half that.
 * The rotation is re-normalised every 4096 samples so it cannot drift.
 */
export function addTone(out, sr, opts) {
  const { freqAt, ampAt, phase = 0, shape = 'sine', freq } = opts;
  const n = out.length;

  if (freq !== undefined && shape === 'sine') {
    const w = TAU * freq / sr;
    const cw = Math.cos(w), sw = Math.sin(w);
    let re = Math.cos(phase), im = Math.sin(phase);
    for (let i = 0; i < n; i++) {
      out[i] += im * ampAt(i / sr);
      const nre = re * cw - im * sw;
      im = re * sw + im * cw;
      re = nre;
      if ((i & 4095) === 4095) {                    // kill accumulated rounding drift
        const inv = 1 / Math.sqrt(re * re + im * im);
        re *= inv; im *= inv;
      }
    }
    return out;
  }

  const constF = freq !== undefined;
  const dpConst = constF ? TAU * freq / sr : 0;
  let ph = phase;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    ph += constF ? dpConst : TAU * freqAt(t) / sr;
    if (ph > TAU) ph -= TAU;
    let s;
    if (shape === 'sine') s = Math.sin(ph);
    else if (shape === 'square') s = Math.sin(ph) >= 0 ? 1 : -1;
    else if (shape === 'tri') s = 2 * Math.abs(2 * (ph / TAU - Math.floor(ph / TAU + 0.5))) - 1;
    else s = 2 * (ph / TAU - Math.floor(ph / TAU + 0.5));   // sawtooth
    out[i] += s * ampAt(t);
  }
  return out;
}

/** Add band-limited-ish noise with an amplitude envelope. */
export function addNoise(out, sr, rand, ampAt) {
  for (let i = 0; i < out.length; i++) out[i] += rand.bi() * ampAt(i / sr);
  return out;
}

/** Fill a scratch buffer with white noise. */
export function fillNoise(buf, rand) {
  for (let i = 0; i < buf.length; i++) buf[i] = rand.bi();
  return buf;
}

/** Paul Kellet's pink filter — in place. */
export function pinkify(buf) {
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  for (let i = 0; i < buf.length; i++) {
    const w = buf[i];
    b0 = 0.99886 * b0 + w * 0.0555179;
    b1 = 0.99332 * b1 + w * 0.0750759;
    b2 = 0.96900 * b2 + w * 0.1538520;
    b3 = 0.86650 * b3 + w * 0.3104856;
    b4 = 0.55000 * b4 + w * 0.5329522;
    b5 = -0.7616 * b5 - w * 0.0168980;
    buf[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
    b6 = w * 0.115926;
  }
  return buf;
}

/**
 * Make a noise loop seamless by cross-fading the tail over the head. Without this every
 * looping noise layer ticks once per loop, which is instantly audible on a bed you hear for
 * ten minutes.
 */
export function seamless(buf, fadeSamples) {
  const n = buf.length;
  const f = Math.min(fadeSamples, (n / 2) | 0);
  for (let i = 0; i < f; i++) {
    const a = i / f;
    const head = buf[i];
    const tail = buf[n - f + i];
    buf[n - f + i] = tail * (1 - a) + head * a;
  }
  return buf.subarray(0, n - f);
}

// --------------------------------------------------------------------- shaping

/** tanh-ish soft clip. `drive` 0 = clean, 1 = squashed. */
export function softClip(buf, drive) {
  const k = 1 + drive * 9;
  const norm = 1 / Math.tanh(k);
  for (let i = 0; i < buf.length; i++) buf[i] = Math.tanh(buf[i] * k) * norm;
  return buf;
}

/** Scale so the peak sits at `peak`. Returns the applied gain. */
export function normalize(buf, peak = 0.98) {
  let m = 0;
  for (let i = 0; i < buf.length; i++) { const a = buf[i] < 0 ? -buf[i] : buf[i]; if (a > m) m = a; }
  if (m < 1e-6) return 0;
  const g = peak / m;
  for (let i = 0; i < buf.length; i++) buf[i] *= g;
  return g;
}

/** Fade the last `ms` to zero so a truncated tail cannot click. */
export function tailFade(buf, sr, ms = 8) {
  const n = Math.min(buf.length, Math.round(sr * ms / 1000));
  for (let i = 0; i < n; i++) buf[buf.length - 1 - i] *= i / n;
  return buf;
}

/** Mix `src` into `dst` at gain g, starting at sample offset. */
export function mixInto(dst, src, offset, g) {
  const n = Math.min(src.length, dst.length - offset);
  for (let i = 0; i < n; i++) dst[offset + i] += src[i] * g;
  return dst;
}

/** RMS of a buffer — used by the audiotest page to show a level bar. */
export function rms(buf) {
  let s = 0;
  for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i];
  return Math.sqrt(s / Math.max(1, buf.length));
}

/**
 * A curve for WaveShaperNode: asymmetric soft clip. Used live on the guitar and the
 * announcer, where it turns a clean saw into something with teeth.
 */
export function makeDriveCurve(amount, n = 1024) {
  const curve = new Float32Array(n);
  const k = 1 + amount * 24;
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(x * k) / Math.tanh(k);
  }
  return curve;
}
